import type { RadioButtonChoice } from 'types/formItem';
import type { Languages, TranslationName } from 'types/translator';
import { normalizeLanguage } from 'utils/helios/language';

type LooseTranslationName = {
  language?: string | null;
  value?: string | null;
};

export type RadioButtonChoiceInput = Omit<RadioButtonChoice, 'translatesName'> & {
  translatesName?: LooseTranslationName[] | null;
};

type RadioButtonDefinition = {
  radioButton?: { items?: RadioButtonChoice[] | null } | null;
};

const getChoiceLabel = (item: RadioButtonChoiceInput, language: string): string => {
  const code = normalizeLanguage(language);
  const translation = item.translatesName?.find((entry) => normalizeLanguage(entry.language) === code)?.value?.trim();

  return translation || item.name;
};

export const getRadioButtonItems = (definition?: RadioButtonDefinition | null): RadioButtonChoiceInput[] => definition?.radioButton?.items ?? [];

export const getRadioButtonStoredValue = (item: { name: string; value?: string | null }): string => {
  const stored = typeof item.value === 'string' ? item.value.trim() : '';

  return stored.length > 0 ? stored : item.name;
};

export const getRadioButtonChoiceLabel = (items: RadioButtonChoiceInput[] | null | undefined, storedValue: string, language: string): string => {
  const match = items?.find((item) => getRadioButtonStoredValue(item) === storedValue);

  if (!match) return storedValue;

  return getChoiceLabel(match, language);
};

export const toRadioButtonOptions = (items: RadioButtonChoiceInput[] | null | undefined, language: string) =>
  (items ?? []).map((item) => {
    const value = getRadioButtonStoredValue(item);

    return {
      label: getChoiceLabel(item, language),
      value,
      id: value,
    };
  });

export const normalizeRadioButtonChoices = (items: RadioButtonChoiceInput[] | null | undefined): RadioButtonChoice[] =>
  (items ?? []).map((item) => {
    const translations = (item.translatesName ?? []).reduce<TranslationName[]>((result, translation) => {
      const text = translation.value?.trim();
      const language = normalizeLanguage(translation.language);

      if (!text || !language || result.some((existing) => existing.language === language)) {
        return result;
      }

      result.push({ language: language as Languages, value: text });

      return result;
    }, []);

    const trimmedValue = typeof item.value === 'string' ? item.value.trim() : '';

    return {
      name: item.name,
      value: trimmedValue.length > 0 ? trimmedValue : null,
      translatesName: translations.length > 0 ? translations : null,
    };
  });
